import axiosInstance from "@/api/axios-instance";

export interface CalendlyGroup {
  uri: string;
  name: string;
}

export interface CalendlyEventType {
  uri: string;
  name: string;
  slug: string | null;
  active: boolean;
  scheduling_url: string | null;
  duration: number | null;
  kind: string | null;
  pooling_type: string | null;
  type: string | null;
  admin_managed: boolean | null;
  owner_type: string | null;
  owner_name: string | null;
}

export interface CalendlyPage<T> {
  items: T[];
  nextPageToken: string | null;
}

export async function getCalendlyGroups(
  pageToken?: string,
): Promise<CalendlyPage<CalendlyGroup>> {
  const r = await axiosInstance.get("/calendly/groups", {
    params: pageToken ? { page_token: pageToken } : {},
  });
  return { items: r.data.groups ?? [], nextPageToken: r.data.next_page_token ?? null };
}

export interface StagingEventType {
  id: string;
  calendly_uri: string;
  name: string | null;
  slug: string | null;
  active: boolean | null;
  scheduling_url: string | null;
  duration_minutes: number | null;
  kind: string | null;
  pooling_type: string | null;
  type: string | null;
  admin_managed: boolean | null;
  owner_type: string | null;
  owner_name: string | null;
  owner_uri: string | null;
}

export interface StagingEventTypesResult {
  eventTypes: StagingEventType[];
  total: number;
  page: number;
  lastPage: number;
  nextPage: number | null;
}

export interface StagingSyncStatus {
  status: "IDLE" | "RUNNING" | "COMPLETED" | "FAILED";
  phase?: "fetching" | "saving" | "cleaning" | "done";
  started_at?: string;
  completed_at?: string | null;
  fetched?: number;
  created?: number;
  updated?: number;
  removed?: number;
  error?: string | null;
}
export async function startStagingSync(): Promise<StagingSyncStatus> {
  return (await axiosInstance.post("/calendly/event-types/sync")).data;
}
export async function getStagingSyncStatus(): Promise<StagingSyncStatus> {
  return (await axiosInstance.get("/calendly/event-types/sync/status")).data;
}

export async function getStagingEventTypes(params: {
  type?: "all" | "solo" | "round_robin";
  search?: string;
  page?: number;
  limit?: number;
}): Promise<StagingEventTypesResult> {
  const r = await axiosInstance.get("/calendly/staging/event-types", {
    params: { ...params, search: params.search || undefined },
  });
  return r.data;
}

export interface CalendlyGroupMember {
  uri: string;
  name: string | null;
  professional: { id: string; name: string; email: string | null } | null;
}

export async function getCalendlyGroupMembers(
  groupUri: string,
): Promise<CalendlyGroupMember[]> {
  const r = await axiosInstance.get("/calendly/group-members", {
    params: { group: groupUri },
  });
  return r.data.members ?? [];
}

export interface DerivedManagedEvent {
  name: string | null;
  eventTypeCount: number;
  minDuration: number | null;
  maxDuration: number | null;
}

export async function getDerivedManagedEvents(): Promise<DerivedManagedEvent[]> {
  const r = await axiosInstance.get("/calendly/derived-managed-events");
  return r.data.managedEvents ?? [];
}

export async function getCalendlyEventTypes(
  adminManaged?: boolean,
  pageToken?: string,
): Promise<CalendlyPage<CalendlyEventType>> {
  // The cursor needs its original filter scope to validate, so keep sending
  // admin_managed alongside page_token (the server drops `count` for paged
  // requests, which is the param Calendly forbids next to a cursor).
  const params = {
    ...(adminManaged === undefined ? {} : { admin_managed: adminManaged }),
    ...(pageToken ? { page_token: pageToken } : {}),
  };
  const r = await axiosInstance.get("/calendly/event-types", { params });
  return {
    items: r.data.eventTypes ?? [],
    nextPageToken: r.data.next_page_token ?? null,
  };
}
